import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { reviewService } from '../../../services/reviewService';
import { useAuth } from '../../../contexts/AuthContext';

const ProductCommentsSection = ({ productId }) => {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [userReview, setUserReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  // Form state
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [hoveredRating, setHoveredRating] = useState(0);

  useEffect(() => {
    if (productId) {
      loadReviews();
    }
  }, [productId]);

  const loadReviews = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [reviewsData, statsData, userReviewData] = await Promise.all([
        reviewService?.getProductReviews(productId),
        reviewService?.getProductReviewStats(productId),
        user ? reviewService?.getUserProductReview(productId) : Promise.resolve(null)
      ]);
      
      setReviews(reviewsData);
      setStats(statsData);
      setUserReview(userReviewData);
      
      if (userReviewData) {
        setRating(userReviewData?.rating);
        setComment(userReviewData?.comment);
      }
    } catch (err) {
      console.error('Error loading reviews:', err);
      setError(err?.message || 'Yorumlar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!user) {
      setError('Yorum yapmak için giriş yapmalısınız.');
      return;
    }
    
    if (!comment?.trim()) {
      setError('Lütfen bir yorum yazın.');
      return;
    }
    
    try {
      setSubmitting(true);
      setError(null);
      
      if (userReview) {
        await reviewService?.updateProductReview(userReview?.id, rating, comment?.trim());
      } else {
        await reviewService?.createProductReview(productId, rating, comment?.trim());
      }
      
      setIsFormOpen(false);
      await loadReviews();
    } catch (err) {
      console.error('Error submitting review:', err);
      setError(err?.message || 'Yorum gönderilirken hata oluştu');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!userReview || !window.confirm('Yorumunuzu silmek istediğinizden emin misiniz?')) {
      return;
    }
    
    try {
      setSubmitting(true);
      setError(null);
      
      await reviewService?.deleteProductReview(userReview?.id);
      
      setRating(5);
      setComment('');
      setIsFormOpen(false);
      await loadReviews();
    } catch (err) {
      console.error('Error deleting review:', err);
      setError(err?.message || 'Yorum silinirken hata oluştu');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = (count, interactive = false) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5]?.map((star) => (
          <button
            key={star}
            type={interactive ? 'button' : undefined}
            disabled={!interactive}
            onClick={() => interactive && setRating(star)}
            onMouseEnter={() => interactive && setHoveredRating(star)}
            onMouseLeave={() => interactive && setHoveredRating(0)}
            className={`${
              interactive ? 'cursor-pointer hover:scale-110' : 'cursor-default'
            } transition-transform`}
          >
            <Icon
              name="Star"
              size={interactive ? 24 : 16}
              className={`${
                star <= (interactive ? (hoveredRating || rating) : count)
                  ? 'fill-yellow-400 text-yellow-400' :'text-gray-300'
              }`}
            />
          </button>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="py-12 flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-xl font-bold text-foreground mb-2">
            Değerlendirmeler
          </h3>
          {stats && stats?.totalReviews > 0 && (
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                {renderStars(Math.round(stats?.averageRating))}
                <span className="text-2xl font-bold text-foreground">
                  {stats?.averageRating?.toFixed(1)}
                </span>
              </div>
              <span className="text-sm text-muted-foreground">
                {stats?.totalReviews} değerlendirme
              </span>
            </div>
          )}
        </div>
        
        {user && (
          <Button
            onClick={() => setIsFormOpen(!isFormOpen)}
            variant={userReview ? 'outline' : 'default'}
            size="sm"
            iconName={userReview ? 'Edit' : 'Plus'}
            iconPosition="left"
          >
            {userReview ? 'Yorumumu Düzenle' : 'Yorum Yap'}
          </Button>
        )}
      </div>
      {/* Error Message */}
      {error && (
        <div className="p-4 rounded-lg bg-error/10 border border-error/20">
          <div className="flex items-start gap-3">
            <Icon name="AlertCircle" size={20} className="text-error mt-0.5" />
            <p className="text-sm text-error">{error}</p>
          </div>
        </div>
      )}
      {/* Review Form */}
      {isFormOpen && user && (
        <form onSubmit={handleSubmit} className="p-6 rounded-xl border border-border bg-card space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Puanlama
            </label>
            {renderStars(rating, true)}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Yorumunuz
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e?.target?.value)}
              placeholder="Ürün hakkındaki düşüncelerinizi paylaşın..."
              rows={4}
              className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
              disabled={submitting}
              required
            />
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              type="submit"
              disabled={submitting || !comment?.trim()}
              iconName="Send"
              iconPosition="left"
            >
              {submitting ? 'Gönderiliyor...' : userReview ? 'Güncelle' : 'Gönder'}
            </Button>
            
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsFormOpen(false);
                if (userReview) {
                  setRating(userReview?.rating);
                  setComment(userReview?.comment);
                } else {
                  setRating(5);
                  setComment('');
                }
              }}
              disabled={submitting}
            >
              İptal
            </Button>
            
            {userReview && (
              <Button
                type="button"
                variant="outline"
                onClick={handleDelete}
                disabled={submitting}
                iconName="Trash2"
                iconPosition="left"
                className="ml-auto text-error hover:bg-error/10"
              >
                Sil
              </Button>
            )}
          </div>
        </form>
      )}
      {/* Reviews List */}
      {reviews?.length === 0 ? (
        <div className="py-12 text-center">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
            <Icon name="MessageSquare" size={32} color="var(--color-muted-foreground)" />
          </div>
          <h4 className="text-lg font-semibold text-foreground mb-2">
            Henüz değerlendirme yok
          </h4>
          <p className="text-sm text-muted-foreground">
            Bu ürün için ilk yorumu siz yapın!
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {reviews?.map((review) => (
            <div
              key={review?.id}
              className="p-6 rounded-xl border border-border bg-card hover:border-primary/20 transition-colors"
            >
              <div className="flex items-start gap-4">
                {/* User Avatar */}
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  {review?.userAvatar ? (
                    <img
                      src={review?.userAvatar}
                      alt={review?.userAvatarAlt}
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    <Icon name="User" size={24} color="var(--color-primary)" />
                  )}
                </div>
                
                {/* Review Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h4 className="font-semibold text-foreground">
                        {review?.userName}
                      </h4>
                      <div className="flex items-center gap-2 mt-1">
                        {renderStars(review?.rating)}
                        <span className="text-xs text-muted-foreground">
                          {review?.date}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-sm text-foreground leading-relaxed">
                    {review?.comment}
                  </p>
                  
                  {/* Review Images */}
                  {review?.imageUrls && review?.imageUrls?.length > 0 && (
                    <div className="flex gap-2 mt-4">
                      {review?.imageUrls?.map((url, index) => (
                        <img
                          key={index}
                          src={url}
                          alt={`Review image ${index + 1}`}
                          className="w-20 h-20 rounded-lg object-cover border border-border"
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Badge Incentive */}
      {user && !userReview && (
        <div className="p-4 rounded-lg bg-primary/10 border border-primary/20">
          <div className="flex items-start gap-3">
            <Icon name="Award" size={20} className="text-primary mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-foreground mb-1">
                Muhabbet Kuşu Rozeti Kazan!
              </h4>
              <p className="text-xs text-muted-foreground">
                Yorum yaparak toplulukta aktif olun ve özel rozetler kazanın.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductCommentsSection;